import { useEffect, useRef } from 'react'

/**
 * Pan/zoom gestures for an SVG canvas: wheel, buttons, and pinch.
 *
 * Both warehouse designers are a viewport (`{ scale, tx, ty }`) over a drawing
 * in millimetres, and both had their own copy of the wheel handler and the
 * zoom-about-a-point arithmetic. Only the clamps differed, so those are the
 * arguments.
 *
 * Pointer events are what make it work by touch: mouse, pen and finger all
 * arrive through one path, so the caller's existing handlers — which read
 * `clientX`/`clientY` — need no change. The three `pointer*` functions each
 * return **true when the gesture consumed the event**, which is the caller's
 * signal to stop: a pinch must not also pan, drag a shape, or save geometry.
 *
 *   onPointerDown={e => { if (g.pointerDown(e)) return; ...own handling }}
 *
 * The caller must also set `touch-action: none` on the canvas. Without it the
 * browser claims the touch for page scroll and none of this ever runs.
 */
export default function useCanvasGestures({
    svgRef,
    vp,
    setVp,
    minScale,
    maxScale,
    // Whether a drag is already in progress. A second finger during a drag is
    // ignored rather than yanking the viewport out from under it.
    isBusy,
    // Called when a pinch begins, so the caller can abandon the pan the first
    // finger had started.
    onPinchStart,
}) {
    // Live pointers by id: one is a pan or a drag, two are a pinch.
    const pointersRef = useRef(new Map())
    const pinchRef = useRef(null)
    // Latches to 'pinch' while a second finger has been down, and clears only
    // when every finger has lifted — releasing one finger must not turn the
    // remaining one into a pan from a stale origin, which makes the drawing jump.
    const gestureRef = useRef(null)

    const clamp = (s) => Math.max(minScale, Math.min(maxScale, s))

    function rect() {
        return svgRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 800, height: 600 }
    }

    // Scale about a point in canvas coordinates, keeping what is under that
    // point where it is.
    function zoomAt(cx, cy, factor) {
        setVp(prev => {
            const scale = clamp(prev.scale * factor)
            const k = scale / prev.scale
            return { scale, tx: cx - (cx - prev.tx) * k, ty: cy - (cy - prev.ty) * k }
        })
    }

    function zoomBy(factor) {
        const r = rect()
        zoomAt(r.width / 2, r.height / 2, factor)
    }

    // Attached by hand rather than through onWheel: React's synthetic wheel
    // handler is passive, so it cannot preventDefault and the page scrolls too.
    useEffect(() => {
        const el = svgRef.current
        if (!el) return
        const onWheel = (e) => {
            e.preventDefault()
            const r = el.getBoundingClientRect()
            zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.1 : 0.9)
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svgRef, minScale, maxScale])

    function pinchGeometry() {
        const pts = [...pointersRef.current.values()]
        if (pts.length < 2) return null
        const r = rect()
        return {
            cx: (pts[0].x + pts[1].x) / 2 - r.left,
            cy: (pts[0].y + pts[1].y) / 2 - r.top,
            dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1,
        }
    }

    function doPinch() {
        const p = pinchRef.current
        const m = pinchGeometry()
        if (!p || !m) return
        const scale = clamp(p.scale * (m.dist / p.dist))
        const k = scale / p.scale
        // Undo the start midpoint at the old scale, redo it at the new one, and
        // follow the midpoint as the fingers travel — so a pinch pans as well.
        setVp({ scale, tx: m.cx - (p.cx - p.tx) * k, ty: m.cy - (p.cy - p.ty) * k })
    }

    function pointerDown(e) {
        // A mouse drag that leaves the canvas keeps tracking; touch gets the
        // same behaviour implicitly.
        if (e.pointerType === 'mouse') e.currentTarget.setPointerCapture?.(e.pointerId)
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        if (pointersRef.current.size < 2) return false
        if (isBusy?.()) return true
        const m = pinchGeometry()
        if (m) {
            onPinchStart?.()
            gestureRef.current = 'pinch'
            pinchRef.current = { dist: m.dist, cx: m.cx, cy: m.cy, scale: vp.scale, tx: vp.tx, ty: vp.ty }
        }
        return true
    }

    function pointerMove(e) {
        if (pointersRef.current.has(e.pointerId)) {
            pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        }
        if (gestureRef.current !== 'pinch') return false
        if (pointersRef.current.size === 2) doPinch()
        return true
    }

    function pointerUp(e) {
        const wasPinch = gestureRef.current === 'pinch'
        pointersRef.current.delete(e.pointerId)
        if (pointersRef.current.size < 2) pinchRef.current = null
        if (pointersRef.current.size === 0) gestureRef.current = null
        return wasPinch
    }

    return { zoomBy, pointerDown, pointerMove, pointerUp }
}
